import { AuthRepository } from 'app/state/auth.repository';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { UrlIdResolverService } from 'app/modules/shared/services/url-id-resolver.service';
import { FeatureRepository, TenantFeatures } from 'app/state/feature.repository';
import { FeatureService } from 'app/state/feature.service';
import { Tenant, TenantsRepository } from 'app/state/tenants.repository';
import { TenantsService } from 'app/state/tenants.service';
import { GeneralDataRepository } from '../../../../helpers/repository/general-data.repository';
import { TrackRender } from 'app/shared/front-perfomance';
import { MessageText, ToastService } from 'app/helpers/services/toastService';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BreadcrumbService } from 'app/modules/shared/components/breadcrumb/breadcrumb.service';

const TABS_OPTIONS: { value: string; label: string; feature?: boolean }[] = [
  { value: 'home', label: 'Home' },
  { value: 'information', label: 'Information' },
  { value: 'spDescription', label: 'SP Description' },
  { value: 'description', label: 'Description' },
  { value: 'questionnaire', label: 'Questionnaire', feature: true },
  { value: 'tags', label: 'Tags' },
  { value: 'folders', label: 'Folders' },
  { value: 'teams', label: 'Teams' },
  { value: 'departments', label: 'Departments' },
  { value: 'checklists', label: 'Checklists' },
  { value: 'msGraphOptions', label: 'Microsoft Graph' },
  { value: 'emailTemplates', label: 'Email Templates' },
];

@UntilDestroy()
@TrackRender()
@Component({
  selector: 'app-tenants-create-page',
  templateUrl: './tenants-create-page.component.html',
  styleUrls: ['./tenants-create-page.component.scss'],
  standalone: false,
})
export class TenantsCreatePageComponent implements OnInit {
  tenant$: Observable<Tenant> | null = null;
  tenant: Tenant | null = null;
  id?: string;
  submitErrors: string[] | null = null;
  isLoading: boolean = true;
  isSaving = false;

  tenantId?: string | null;

  selectedTab: string = 'home';

  /** The questionnaire endpoints are Superadmin-only, so partner managers never see the tab. */
  isSuperAdmin = false;

  /**
   * Same pair the sidebar and the home page gate on, but read from THIS
   * tenant's own feature assignment rather than the active tenant's — the page
   * edits any tenant. It is also deliberately the raw assignment: the runtime
   * feature list drops Questionnaire until the Danish questions are finished,
   * and gating the tab on that would leave no way to write them in the first
   * place.
   */
  get tenantHasQuestionnaire(): boolean {
    const names = this.tenant?.features?.map((x) => x.name) ?? [];
    return (
      this.isSuperAdmin &&
      names.includes(TenantFeatures.Questionnaire) &&
      names.includes(TenantFeatures.PersonalPlan)
    );
  }

  tabsOptions = TABS_OPTIONS.filter((tab) => !tab.feature);

  /** Feature-gated tabs appear once the tenant is known to have the feature. */
  private applyTabVisibility(): void {
    this.tabsOptions = TABS_OPTIONS.filter(
      (tab) => !tab.feature || this.tenantHasQuestionnaire,
    );

    // The tab disappears with the feature; do not strand the page on it.
    if (this.selectedTab === 'questionnaire' && !this.tenantHasQuestionnaire) {
      this.selectedTab = 'home';
    }
  }

  constructor(
    private tenantService: TenantsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private featureService: FeatureService,
    public featureRepo: FeatureRepository,
    private sidebarRepo: GeneralDataRepository,
    public toastService: ToastService,
    private breadcrumbService: BreadcrumbService,
    private authRepo: AuthRepository,
  ) {}

  ngOnInit(): void {
    this.authRepo.isSuperAdmin$.pipe(untilDestroyed(this)).subscribe((v) => {
      this.isSuperAdmin = v;
      this.applyTabVisibility();
    });
    this.activatedRoute.paramMap
      .pipe(untilDestroyed(this))
      .subscribe((params) => {
        var idFromrout = params.get('id');
        this.tenantId = idFromrout !== 'new' ? idFromrout : null;
        if (idFromrout && idFromrout !== 'new') {
          this.loadTenant(idFromrout);
        } else {
          // New tenant: store the translation KEY so it resolves reactively.
          this.isLoading = false;
          this.breadcrumbService.setDynamicLabel('tenantName', 'createTenant');
        }
      });

    this.loadData();
  }

  loadTenant(tenantId: string) {
    this.tenantService
      .loadOne(tenantId)
      .pipe(untilDestroyed(this))
      .subscribe((tenant) => {
        this.tenant = tenant;
        this.applyTabVisibility();
        this.id = tenant.id;
        this.tenantId = tenant.id;
        this.isLoading = false;
        // Store the entity name directly; transloco passes unknown keys through.
        this.breadcrumbService.setDynamicLabel('tenantName', tenant.name);
      });
  }

  reloadTenant() {
    if (this.id) {
      this.loadTenant(this.id);
    }
  }

  loadData() {
    var currTenantId = localStorage.getItem('activeTenantId');
    if (!currTenantId) {
      currTenantId = 'empty';
    }
    this.featureService.loadFeatures(currTenantId).subscribe(() => {
      this.isLoading = false;

      setTimeout(() => {
        console.log('APP_READY_TenantPage');
      }, 3000);
    });
  }

  createTenant(tenant: Partial<Tenant>) {
    if (this.isSaving) {
      return;
    }

    this.submitErrors = null;
    const isNewTenant = !this.id;
    let updateResult: Observable<Tenant>;

    if (!this.id) {
      updateResult = this.tenantService.add(tenant);
    } else {
      updateResult = this.tenantService.update(this.id, tenant);
    }

    this.isSaving = true;
    updateResult.pipe(untilDestroyed(this)).subscribe({
      complete: () => {
        this.isSaving = false;
        this.sidebarRepo.updateAllGeneralData(true);

        if (isNewTenant) {
          this.router.navigate(['/tenants']);
          return;
        }

        this.reloadTenant();
        this.toastService.showSuccess(MessageText.saved);
      },
      error: (data) => {
        this.isSaving = false;
        this.submitErrors = data;
      },
    });
  }

  formatText(text: string): string {
    return text.toLowerCase().replace(/\s+/g, '-');
  }

  getInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return '?';
    }
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }

  changeTabs(tab: string) {
    this.selectedTab = tab;
  }
}
